import { type FC } from "react";

interface SkeletonTextProps {
  lines?: number;
  widths?: string[];
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

interface SkeletonBoxProps {
  className?: string;
  rounded?: string;
}

interface SkeletonKPIProps {
  className?: string;
}

interface SkeletonTableProps {
  rows?: number;
  cols?: number;
  className?: string;
}

interface SkeletonHeroProps {
  className?: string;
}

const shimmerClass = "animate-shimmer";

export const SkeletonText: FC<SkeletonTextProps> = ({ lines = 1, widths, className = "", size = "md" }) => {
  const defaultWidths: Record<typeof size, string> = { sm: "w-full", md: "w-full", lg: "w-3/4", xl: "w-5/6" };
  const sizeHeights: Record<string, string> = { sm: "h-3", md: "h-4", lg: "h-5", xl: "h-6" };
  const baseHeight = sizeHeights[size];
  const lineCount = lines;

  return (
    <div className={`space-y-2 ${className}`} role="status" aria-label="Cargando texto">
      {Array.from({ length: lineCount }).map((_, i) => {
        const width = (widths && widths[i]) ? widths[i] : defaultWidths[size];
        return (
          <div
            key={i}
            className={`${baseHeight} ${width} rounded-full bg-background-100 ${shimmerClass}`}
          />
        );
      })}
    </div>
  );
};

export const SkeletonBox: FC<SkeletonBoxProps> = ({ className = "", rounded = "rounded-xl" }) => {
  return (
    <div
      className={`${rounded} bg-background-100 ${shimmerClass} ${className}`}
      role="status"
      aria-label="Cargando contenido"
    />
  );
};

export const SkeletonKPI: FC<SkeletonKPIProps> = ({ className = "" }) => {
  return (
    <div className={`rounded-2xl border border-background-200/60 bg-white p-5 ${className}`} role="status" aria-label="Cargando indicador">
      <div className="flex items-center gap-3 mb-3">
        <SkeletonBox className="w-10 h-10 rounded-xl flex-shrink-0" />
        <SkeletonBox className="w-16 h-8 rounded-lg flex-shrink-0" />
      </div>
      <SkeletonText lines={1} widths={["w-2/3"]} size="sm" className="mb-1.5" />
      <SkeletonText lines={1} widths={["w-1/2"]} size="sm" />
    </div>
  );
};

export const SkeletonTable: FC<SkeletonTableProps> = ({ rows = 5, cols = 5, className = "" }) => {
  return (
    <div className={`bg-white rounded-2xl border border-background-200/60 overflow-hidden ${className}`} role="status" aria-label="Cargando tabla">
      {/* Header */}
      <div className="flex items-center gap-4 px-5 py-3 border-b border-background-100/80 bg-background-50/50">
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonBox key={`h-${i}`} className="h-3 rounded-full flex-1 min-w-[40px]" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} className="flex items-center gap-4 px-5 py-3.5 border-b border-background-100/80">
          <SkeletonBox className="w-3 h-3 rounded-full flex-shrink-0" />
          {Array.from({ length: cols - 1 }).map((_, colIdx) => (
            <SkeletonBox
              key={`r${rowIdx}-c${colIdx}`}
              className="h-3.5 rounded-full flex-1 min-w-[40px]"
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export const SkeletonHero: FC<SkeletonHeroProps> = ({ className = "" }) => {
  return (
    <div className={`relative overflow-hidden rounded-3xl p-6 md:p-8 bg-background-100 ${shimmerClass} ${className}`} role="status" aria-label="Cargando cabecera">
      <div className="space-y-3">
        <SkeletonBox className="w-40 h-4 rounded-full" />
        <SkeletonBox className="w-72 md:w-96 h-8 rounded-lg" />
        <SkeletonBox className="w-full max-w-md h-4 rounded-full" />
      </div>
      <div className="mt-6">
        <SkeletonBox className="w-36 h-9 rounded-xl" />
      </div>
    </div>
  );
};

export const SkeletonCard: FC<{ className?: string; hasImage?: boolean }> = ({ className = "", hasImage = false }) => {
  return (
    <div className={`card-modern p-5 ${className}`} role="status" aria-label="Cargando tarjeta">
      <div className="flex items-center justify-between mb-4">
        <SkeletonText lines={1} widths={["w-32"]} size="sm" />
        <SkeletonText lines={1} widths={["w-20"]} size="sm" />
      </div>
      {hasImage && <SkeletonBox className="w-full h-32 rounded-xl mb-4" />}
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <SkeletonBox className="w-2 h-2 rounded-full flex-shrink-0" />
            <SkeletonText lines={1} widths={["w-full"]} size="sm" />
          </div>
        ))}
      </div>
    </div>
  );
};

export const SkeletonQuickActions: FC<{ count?: number; className?: string }> = ({ count = 6, className = "" }) => {
  return (
    <div className={className} role="status" aria-label="Cargando acciones">
      <SkeletonText lines={1} widths={["w-32"]} size="sm" className="mb-3" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-background-200/60 bg-white p-4 flex flex-col items-center gap-2.5">
            <SkeletonBox className="w-11 h-11 rounded-xl" />
            <SkeletonBox className="w-16 h-3 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
};